export type OrganizationAccountRole =
  | 'organization_owner'
  | 'organization_admin'
  | 'organization_member';

export type PlatformAccountRole =
  | 'platform_owner'
  | 'platform_admin'
  | 'support_operator';

export type MvpAccountRole = OrganizationAccountRole | PlatformAccountRole;

/**
 * Canonical organization account role, exported under the doctrine name for
 * authorization code. Alias of OrganizationAccountRole.
 */
export type AccountRole = OrganizationAccountRole;

/**
 * Principal role across BOTH planes. Use ONLY where a single shared persisted
 * or display field legitimately carries either an organization AccountRole or a
 * platform role (e.g. the shared `account_role` column, or a cross-plane
 * display label). Organization and platform authority remain separate
 * everywhere else — never widen one plane's union with the other.
 */
export type CanonicalPrincipalRole = AccountRole | PlatformAccountRole;

/**
 * Non-authorizing platform specialization metadata. A PlatformFunction NEVER
 * grants access — authority always resolves from the account/platform role +
 * active membership + scoped relationships + capabilities + explicit rights +
 * lifecycle + AAL/JIT. Functions are used only for routing, staffing, filtering
 * and display.
 */
export type PlatformFunction =
  | 'security'
  | 'commercial'
  | 'implementation'
  | 'support'
  | 'operations';

export const PLATFORM_FUNCTIONS: readonly PlatformFunction[] = [
  'security', 'commercial', 'implementation', 'support', 'operations',
] as const;

/**
 * Retired granular platform labels -> the non-authorizing function they
 * described. MIGRATION / EVIDENCE ONLY: never consult this to make an access
 * decision. Authority for these accounts is `platform_admin` (or
 * `support_operator` for the old support_admin) via `account_role`.
 */
export const LEGACY_PLATFORM_LABEL_FUNCTION: Readonly<Record<string, PlatformFunction>> = {
  system_admin: 'operations',
  security_admin: 'security',
  commercial_admin: 'commercial',
  implementation_admin: 'implementation',
  support_admin: 'support',
};

/**
 * Explicit, lifecycle-managed platform capability grant. This is how a platform
 * admin obtains DIFFERENTIATED control-plane powers beyond the reviewed base
 * role policy — never from `platform_functions` (which are descriptive only)
 * and never implicitly from a legacy label.
 *
 * `capability` is kept as a raw string at the type layer; the resolver
 * validates it is a real platform-control capability and fails closed on
 * unknown / customer-resource capabilities. In production these records come
 * from a server-managed table, not a browser-editable profile field.
 */
export interface PlatformCapabilityAssignment {
  capability: string;
  status: 'active' | 'suspended' | 'revoked';
  effectiveFrom?: string;
  effectiveUntil?: string;
  grantedBy?: string;
  source: 'policy' | 'manual' | 'migration' | 'demo';
}

export type ScopedAccessLevel = 'view' | 'review' | 'contribute' | 'manage';

export type ScopedRelationshipType =
  | 'workspace_member'
  | 'company_representative'
  | 'project_manager'
  | 'project_contributor'
  | 'partner_representative'
  | 'vendor_representative'
  | 'investor_representative'
  | 'investment_manager'
  | 'reporting_recipient'
  | 'decision_participant'
  | 'validator'
  | 'observer';

export type AuthorityLifecycleStatus = 'invited' | 'active' | 'suspended' | 'revoked' | 'expired';

/**
 * Client-side (browser/demo) shape of a scoped relationship record.
 * This is the field carried on `User.scopedRelationships` and is the executable
 * input consumed by the canonical resolver in `lib/auth/scopedRelationshipResolver.ts`.
 * Production authority is still enforced server-side (RLS + `ax1_has_scoped_access`);
 * this client-side record must mirror the same shape so UI-side filtering cannot
 * grant more than the server would.
 */
export type ScopedResourceType =
  | 'workspace'
  | 'company'
  | 'project'
  | 'investment'
  | 'report'
  | 'decision'
  /** Object-scoped validator targets (not project-wide roles). */
  | 'task'
  | 'gate'
  | 'milestone';

export interface UserScopedRelationship {
  type: ScopedRelationshipType;
  resourceType: ScopedResourceType;
  resourceId: string;
  accessLevel: ScopedAccessLevel;
  status: AuthorityLifecycleStatus;
  effectiveFrom?: string | null;
  effectiveUntil?: string | null;
  /** Only set for relationships that represent a company on a project/company resource. */
  representedCompanyId?: string | null;
}

export interface ScopedRelationshipAuthority {
  userId: string;
  organizationId: string;
  workspaceId: string;
  resourceType: string;
  resourceId: string;
  representedCompanyId?: string | null;
  relationshipType: ScopedRelationshipType;
  accessLevel: ScopedAccessLevel;
  status: AuthorityLifecycleStatus;
  effectiveFrom: Date;
  effectiveUntil?: Date | null;
  assignedBy: string;
  approvedBy?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export const MVP_ORGANIZATION_ACCOUNT_ROLES: readonly OrganizationAccountRole[] = [
  'organization_owner',
  'organization_admin',
  'organization_member',
] as const;

export const MVP_PLATFORM_ACCOUNT_ROLES: readonly PlatformAccountRole[] = [
  'platform_owner',
  'platform_admin',
  'support_operator',
] as const;

export const SCOPED_ACCESS_LEVELS: readonly ScopedAccessLevel[] = [
  'view',
  'review',
  'contribute',
  'manage',
] as const;

const ACCESS_LEVEL_RANK: Readonly<Record<ScopedAccessLevel, number>> = {
  view: 0,
  review: 1,
  contribute: 2,
  manage: 3,
};

export function hasScopedAccessLevel(
  actual: ScopedAccessLevel,
  required: ScopedAccessLevel,
): boolean {
  return ACCESS_LEVEL_RANK[actual] >= ACCESS_LEVEL_RANK[required];
}

export function isAuthorityEffective(
  authority: Pick<ScopedRelationshipAuthority, 'status' | 'effectiveFrom' | 'effectiveUntil'>,
  at: Date = new Date(),
): boolean {
  return authority.status === 'active'
    && authority.effectiveFrom.getTime() <= at.getTime()
    && (authority.effectiveUntil == null || authority.effectiveUntil.getTime() > at.getTime());
}

export function isMvpAccountRole(role: string | null | undefined): role is MvpAccountRole {
  return Boolean(role && (
    MVP_ORGANIZATION_ACCOUNT_ROLES.includes(role as OrganizationAccountRole)
    || MVP_PLATFORM_ACCOUNT_ROLES.includes(role as PlatformAccountRole)
  ));
}/**
 * AX1 Authority Model - Canonical Foundation (Wave 7 / Prompt 36)
 *
 * Doctrine:
 * - Authority is not a role.
 * - Authority is not participation.
 * - Authority is not affiliation.
 *
 * This layer is intentionally orthogonal to:
 * - UserRole / WorkspaceRole / ProjectRole
 * - retired participation plane
 * - actionEligibility
 *
 * Type-only module. No services, UI, or persistence wiring.
 */

/**
 * Canonical authority categories for project and workspace operations.
 */
export type AuthorityType =
    | 'execution_authority'
    | 'capital_authority'
    | 'governance_authority'
    | 'validation_authority';

/**
 * Provenance of an authority assignment.
 *
 * - manual: explicitly assigned by an authorized operator
 * - derived: inferred from legacy context; not explicit operator assignment
 * - migration: backfilled during controlled migration
 */
export type AuthorityAssignmentSource = 'manual' | 'derived' | 'migration';

/**
 * Canonical persisted authority assignment contract.
 */
export interface AuthorityAssignment {
    id: string;
    workspaceId: string;
    projectId: string;
    authorityType: AuthorityType;
    userId: string;
    companyId?: string;
    isPrimary?: boolean;
    source: AuthorityAssignmentSource;
    createdAt: string;
    updatedAt: string;
}

/**
 * Presentation model for authority surfaces.
 *
 * This is a pure display contract built from AuthorityAssignment and joined
 * user/company display context.
 */
export interface AuthorityDisplayModel {
    assignmentId?: string;
    authorityType: AuthorityType;
    authorityTypeLabel: string;
    userId: string;
    userName: string;
    userAvatar?: string;
    companyId?: string;
    companyName?: string;
    isPrimary: boolean;
    source: AuthorityAssignmentSource;
    sourceLabel: string;
}

/**
 * Canonical display labels for authority types.
 */
export const AUTHORITY_TYPE_LABELS: Record<AuthorityType, string> = {
    execution_authority: 'Execution Authority',
    capital_authority: 'Capital Authority',
    governance_authority: 'Governance Authority',
    validation_authority: 'Validation Authority',
};

/**
 * Canonical display labels for assignment provenance.
 */
export const AUTHORITY_SOURCE_LABELS: Record<AuthorityAssignmentSource, string> = {
    manual: 'Manual',
    derived: 'Derived',
    migration: 'Migration',
};
